import type {
  TypeDefaults,
  TypeDefaultsField,
} from '@components/ui/cards/TypeDefaultsPreview';
import { mapAppointmentTypeToDefaults } from '@components/ui/cards/TypeDefaultsPreview';

import type {
  CleanupTypeVersion,
  DivergentGroup,
  FixtureType,
  PilotFixture,
  WizardData,
} from '../types';

const toVersion = (row: FixtureType): CleanupTypeVersion => ({
  id: row.encounter_type_id,
  name: row.encounter_type,
  provider: row.provider,
  usage: row.usage,
  ownerScope: row.owner_scope === 'organization' ? 'organization' : 'provider',
  templateId: row.template_id ?? null,
  lastUsedAt: row.last_used_at ?? null,
  templateTitle: row.template_title,
  templateScope: row.template_scope,
  color: row.color,
  defaults: mapAppointmentTypeToDefaults(
    {
      template_id: row.template_id,
      default_duration: row.default_duration,
      default_modality: row.default_modality,
      default_place_of_service: row.default_place_of_service,
      default_cpt_codes: row.default_cpt_codes,
      default_form_rules: row.default_action_rules?.form_rules ?? null,
      default_reminder_rules: row.default_action_rules?.reminder_rules ?? null,
    },
    { templateName: row.template_title }
  ),
  rawDefaults: {
    default_duration: row.default_duration ?? null,
    default_modality: row.default_modality ?? null,
    default_place_of_service: row.default_place_of_service ?? null,
    default_cpt_codes: row.default_cpt_codes ?? null,
    default_icd10_codes: row.default_icd10_codes ?? null,
    telehealth: row.telehealth ?? null,
    default_action_rules: row.default_action_rules ?? null,
  },
});

const byUsageDesc = (a: CleanupTypeVersion, b: CleanupTypeVersion): number =>
  b.usage - a.usage || a.name.localeCompare(b.name);

/** Comparable value per diffable field. */
const fieldValue = (defaults: TypeDefaults, field: TypeDefaultsField): string =>
  JSON.stringify(
    {
      template: defaults.templateName ?? (defaults.templateSet ? 'set' : null),
      duration: defaults.durationLabel,
      modality: defaults.modalityLabel,
      placeOfService: defaults.placeOfServiceCode,
      cptCodes: defaults.cptCodes,
      forms: defaults.forms,
      reminders: defaults.reminders,
    }[field]
  );

const DIFFABLE_FIELDS: TypeDefaultsField[] = [
  'template',
  'duration',
  'modality',
  'placeOfService',
  'cptCodes',
  'forms',
  'reminders',
];

/** Fields whose values differ across a group's versions. */
export const computeDiffFields = (
  versions: CleanupTypeVersion[]
): TypeDefaultsField[] =>
  DIFFABLE_FIELDS.filter(
    (field) =>
      new Set(versions.map((v) => fieldValue(v.defaults, field))).size > 1
  );

/** Resolution-map key for a Step 2½ personal cluster. */
export const personalKey = (nameKey: string): string => `personal:${nameKey}`;

/**
 * Cluster key for personal types: junk suffixes stripped so "APA Progress
 * notes", "APA Progress notes Copy" and the date-stamped variants group as
 * one logical type. Deliberately conservative — only markers the fleet scan
 * validated (copy, numeric date-stamps, separator noise); word differences
 * like "PsychoTherapy" keep clusters apart.
 */
export const normalizePersonalKey = (name: string): string =>
  name
    .toLowerCase()
    .replace(/\bcopy(\s*\d*)\b/g, ' ')
    .replace(/\b\d{1,2}[-/.]\d{1,2}[-/.](\d{2}|\d{4})\b/g, ' ')
    .replace(/[_\-·.]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Derive the wizard's working data from a pilot fixture (which carries the
 * promotion dry-run's per-row dispositions). Pure — same in stories and app.
 */
export const deriveWizardData = (fixture: PilotFixture): WizardData => {
  // Layer-1 auto-archived types live only in the Archived step (with restore)
  // — never as live candidates in the judgment lanes or the quick-wins pool.
  const active = fixture.types.filter((t) => !t.archived);
  const byReason = (reason: string): FixtureType[] =>
    active.filter((t) => t.plan?.reason === reason);

  const divergentRows = byReason('divergent_pending_wizard');
  const grouped = new Map<string, FixtureType[]>();
  for (const row of divergentRows) {
    const key = row.group?.name_key ?? row.encounter_type.trim().toLowerCase();
    grouped.set(key, [...(grouped.get(key) ?? []), row]);
  }

  const divergentGroups: DivergentGroup[] = [...grouped.entries()]
    .map(([nameKey, rows]) => {
      // The group's canonical (already promoted/promoting) belongs in the
      // comparison too — it's the suggested winner.
      const canonicalId = rows[0]?.group?.canonical ?? null;
      const canonicalRow = canonicalId
        ? active.find((t) => t.encounter_type_id === canonicalId)
        : undefined;
      const versions = [
        ...(canonicalRow ? [toVersion(canonicalRow)] : []),
        ...rows.map(toVersion),
      ].sort(byUsageDesc);
      return {
        nameKey,
        displayName: versions[0]?.name ?? nameKey,
        versions,
        diffFields: computeDiffFields(versions),
      };
    })
    .sort((a, b) => b.versions.length - a.versions.length);

  const personalRows = byReason('user_template_blocked');
  const personalGrouped = new Map<string, FixtureType[]>();
  for (const row of personalRows) {
    const key = normalizePersonalKey(row.encounter_type);
    personalGrouped.set(key, [...(personalGrouped.get(key) ?? []), row]);
  }
  const personalClusters: DivergentGroup[] = [...personalGrouped.entries()]
    .filter(([, rows]) => rows.length >= 2)
    .map(([nameKey, rows]) => {
      const versions = rows.map(toVersion).sort(byUsageDesc);
      return {
        nameKey,
        displayName: versions[0]?.name ?? nameKey,
        versions,
        diffFields: computeDiffFields(versions),
      };
    })
    .sort(
      (a, b) =>
        b.versions.reduce((s, v) => s + v.usage, 0) -
        a.versions.reduce((s, v) => s + v.usage, 0)
    );

  const providers = new Set(
    fixture.types.map((t) => t.provider).filter(Boolean)
  );

  return {
    snapshot: {
      orgLabel: fixture.org,
      totalTypes: fixture.types.length,
      providerCount: providers.size,
      autoPromoteCount: active.filter((t) => t.plan?.action === 'promote')
        .length,
      autoMergeCount: active.filter((t) => t.plan?.action === 'merge').length,
      divergentGroupCount: divergentGroups.length,
      personalClusterCount: personalClusters.length,
      personalTypeCount: byReason('user_template_blocked').length,
      alreadySharedCount: byReason('already_org_scope').length,
      zeroUsageCount: byReason('zero_usage_group').length,
      archivedCount: fixture.types.filter((t) => t.archived).length,
    },
    sharedTypes: active
      .filter(
        (t) =>
          t.plan?.action === 'promote' || t.plan?.reason === 'already_org_scope'
      )
      .map(toVersion)
      .sort(byUsageDesc),
    divergentGroups,
    personalTypes: personalRows.map(toVersion).sort(byUsageDesc),
    personalClusters,
    archivedTypes: fixture.types
      .filter((t) => t.archived)
      .map(toVersion)
      .sort((a, b) => a.name.localeCompare(b.name)),
  };
};
